/**
 * TSS Shared Configuration
 *
 * Loads configuration from Vite environment variables.
 * All VITE_* variables are embedded at build time.
 *
 * Required Variables:
 *   VITE_USER_POOL_ID         - Cognito User Pool ID
 *   VITE_USER_POOL_CLIENT_ID  - Cognito App Client ID
 *   VITE_API_ENDPOINT         - API Gateway endpoint URL
 *
 * Optional Variables:
 *   VITE_AWS_REGION           - AWS region (default: us-east-1)
 *   VITE_COGNITO_DOMAIN       - Cognito hosted UI domain (for OAuth flows)
 *   VITE_APP_TYPE             - 'coach' or 'athlete'
 */

export type AppType = 'coach' | 'athlete';

export interface TSSConfig {
  // App Type
  appType: AppType;

  // AWS Region
  region: string;

  // Cognito Configuration
  cognito: {
    userPoolId: string;
    userPoolClientId: string;
    domain: string;
  };

  // OAuth Configuration
  oauth: {
    redirectSignIn: string;
    redirectSignOut: string;
    providers: {
      google: boolean;
      apple: boolean;
    };
  };

  // API Configuration
  api: {
    endpoint: string;
  };

  // Signup Control
  // When false, only login is allowed (invite-only mode)
  signupEnabled: boolean;

  // Cross-app Navigation URLs
  crossApp: {
    coachPortalUrl: string;
    athletePortalUrl: string;
  };
}

// Default config factory - apps will call this with their env vars
export function createConfig(): TSSConfig {
  const appType = (import.meta.env.VITE_APP_TYPE as AppType) || 'athlete';

  return {
    appType,
    region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
      domain: import.meta.env.VITE_COGNITO_DOMAIN || '',
    },
    oauth: {
      redirectSignIn: typeof window !== 'undefined' ? `${window.location.origin}/callback` : '',
      redirectSignOut: typeof window !== 'undefined' ? `${window.location.origin}/` : '',
      providers: {
        google: import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true',
        apple: import.meta.env.VITE_OAUTH_APPLE_ENABLED === 'true',
      },
    },
    api: {
      endpoint: import.meta.env.VITE_API_ENDPOINT || '',
    },
    signupEnabled: import.meta.env.VITE_SIGNUP_ENABLED === 'true',
    crossApp: {
      coachPortalUrl: import.meta.env.VITE_COACH_PORTAL_URL || '',
      athletePortalUrl: import.meta.env.VITE_ATHLETE_PORTAL_URL || '',
    },
  };
}

// Singleton config instance
let _config: TSSConfig | null = null;

export function getConfig(): TSSConfig {
  if (!_config) {
    _config = createConfig();
  }
  return _config;
}

// Allow apps to override config
export function setConfig(config: TSSConfig): void {
  _config = config;
}

// Validate required configuration
export function validateConfig(config: TSSConfig = getConfig()): { valid: boolean; missing: string[] } {
  const missing: string[] = [];

  if (!config.cognito.userPoolId) missing.push('VITE_USER_POOL_ID');
  if (!config.cognito.userPoolClientId) missing.push('VITE_USER_POOL_CLIENT_ID');
  if (!config.api.endpoint) missing.push('VITE_API_ENDPOINT');

  return {
    valid: missing.length === 0,
    missing,
  };
}

// For backwards compatibility
export const config = {
  get region() { return getConfig().region; },
  get cognito() { return getConfig().cognito; },
  get oauth() { return getConfig().oauth; },
  get api() { return getConfig().api; },
  get appType() { return getConfig().appType; },
  get signupEnabled() { return getConfig().signupEnabled; },
  get crossApp() { return getConfig().crossApp; },
};
